import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { IconButton, Icons, SidePanel } from '@usepepper/sprout-kit-ui';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { Spacing } from '@usepepper/sprout-kit';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const CLOSE_ICON_SIZE = 24;

/**
 * Props for {@link ItemDetailContentHeader}.
 */
export interface ItemDetailContentHeaderProps {
  /**
   * Supplier-provided external item identifier (SKU). Joined to the pack
   * display string with a separator. When null, the separator is also hidden
   * to avoid a trailing " · ".
   */
  externalItemId: string | null;
  /**
   * Omits the leading action, for hosts that supply their own dismissal
   * affordance such as `PushView.Header`.
   */
  hideAction?: boolean;
  /** First image URL for the variant pack; null shows the placeholder icon. */
  imageUrl: string | null;
  /** Item display name. Truncated to two lines. */
  itemName: string;
  /**
   * When provided, the header swaps the close X for a back arrow that calls
   * this handler. Used to pop the panel's variant-pack drill-down stack.
   */
  onBack?: () => void;
  /** Called when the close X is pressed. Ignored when `onBack` is set. */
  onClose: () => void;
  /** Pre-formatted abbreviated pack size string (e.g. "12 LB"). */
  packDisplayString: string;
}

/**
 * Header for the item detail panel. Shows either a close X or a back arrow
 * (when `onBack` is provided) plus the item identity row
 * (image · name · pack · external item ID).
 *
 * @param props - {@link ItemDetailContentHeaderProps}.
 * @returns The rendered header.
 */
export const ItemDetailContentHeader: React.FC<
  ItemDetailContentHeaderProps
> = ({
  externalItemId,
  hideAction = false,
  imageUrl,
  itemName,
  onBack,
  onClose,
  packDisplayString,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  const subtitleText = [packDisplayString, externalItemId]
    .filter(Boolean)
    .join(' · ')
    .trim();

  const imageURLs = useMemo(() => (imageUrl ? [imageUrl] : []), [imageUrl]);

  const ActionIcon = onBack ? Icons.ArrowLeft : Icons.X;
  const onActionPress = onBack ?? onClose;
  const actionA11yLabel = i18n.t(
    onBack
      ? 'sales_portal.order_entry.item_detail.a11y.back'
      : 'sales_portal.order_entry.item_detail.a11y.close',
  );

  return (
    <View style={styles.container}>
      {hideAction ? null : (
        <SidePanel.Header
          leadingActions={
            <IconButton
              aria-label={actionA11yLabel}
              icon={<ActionIcon size={CLOSE_ICON_SIZE} />}
              onPress={onActionPress}
              size="$sm"
              variant="tertiary"
            />
          }
        />
      )}
      <View style={styles.itemRow}>
        <ItemImage
          alt={itemName}
          cachePolicy="memory-disk"
          imageURLs={imageURLs}
          size={Spacing.SPACE_3XLARGE}
        />
        <View style={styles.itemTextColumn}>
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={2}
            textTransform="capitalize"
            variant="paragraph-base-600"
          >
            {itemName}
          </Typography>
          {subtitleText ? (
            <Typography
              color={theme.sk.color.text.tertiary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {subtitleText}
            </Typography>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
  },
  itemRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  itemTextColumn: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));
